import React, { useEffect, ReactNode } from "react";
import { useBranding } from "./brandingContext";

interface WhitelabelTheme {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  /** Page canvas / card surface / body text tokens. Defaults preserve the
   *  standard light look; tenants may override via subdomain branding. */
  canvasColor: string;
  surfaceColor: string;
  textColor: string;
  fontFamily: string;
  borderRadius: string;
  shadows: {
    sm: string;
    md: string;
    lg: string;
  };
}

const defaultTheme: WhitelabelTheme = {
  primaryColor: "240 5.9% 10%",
  secondaryColor: "240 4.8% 95.9%",
  accentColor: "240 5.9% 10%",
  canvasColor: "#f9fafb", // gray-50 — the current page background
  surfaceColor: "#ffffff",
  textColor: "#111827", // gray-900
  fontFamily: "Inter, ui-sans-serif, system-ui",
  borderRadius: "0.5rem",
  shadows: {
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
  },
};

interface WhitelabelProviderProps {
  children: ReactNode;
}

export const WhitelabelProvider: React.FC<WhitelabelProviderProps> = ({
  children,
}) => {
  const {
    isWhitelabel: isBrandedSubdomain,
    subdomainConfig,
    template,
  } = useBranding();

  const applyTheme = (newTheme: WhitelabelTheme) => {
    // Apply CSS custom properties
    const root = document.documentElement;
    root.style.setProperty("--whitelabel-primary", newTheme.primaryColor);
    root.style.setProperty("--whitelabel-secondary", newTheme.secondaryColor);
    root.style.setProperty("--whitelabel-accent", newTheme.accentColor);
    root.style.setProperty("--whitelabel-canvas", newTheme.canvasColor);
    root.style.setProperty("--whitelabel-surface", newTheme.surfaceColor);
    root.style.setProperty("--whitelabel-text", newTheme.textColor);
    root.style.setProperty("--whitelabel-font", newTheme.fontFamily);
    root.style.setProperty("--whitelabel-radius", newTheme.borderRadius);
    root.style.setProperty("--whitelabel-shadow-sm", newTheme.shadows.sm);
    root.style.setProperty("--whitelabel-shadow-md", newTheme.shadows.md);
    root.style.setProperty("--whitelabel-shadow-lg", newTheme.shadows.lg);

    // Add whitelabel class to body
    document.body.classList.add("whitelabel-theme");
  };

  const resetTheme = () => {
    // Remove CSS custom properties
    const root = document.documentElement;
    const whitelabelProperties = [
      "--whitelabel-primary",
      "--whitelabel-secondary",
      "--whitelabel-accent",
      "--whitelabel-canvas",
      "--whitelabel-surface",
      "--whitelabel-text",
      "--whitelabel-font",
      "--whitelabel-radius",
      "--whitelabel-shadow-sm",
      "--whitelabel-shadow-md",
      "--whitelabel-shadow-lg",
    ];

    whitelabelProperties.forEach((prop) => {
      root.style.removeProperty(prop);
    });

    // Remove whitelabel class from body
    document.body.classList.remove("whitelabel-theme");
  };

  // Apply theme when subdomain config changes
  useEffect(() => {
    if (isBrandedSubdomain && subdomainConfig) {
      const borderRadius =
        "borderRadius" in subdomainConfig &&
        typeof subdomainConfig.borderRadius === "string"
          ? subdomainConfig.borderRadius
          : defaultTheme.borderRadius;
      const whitelabelTheme: WhitelabelTheme = {
        primaryColor: subdomainConfig.primaryColor || defaultTheme.primaryColor,
        secondaryColor:
          subdomainConfig.secondaryColor || defaultTheme.secondaryColor,
        accentColor: subdomainConfig.primaryColor || defaultTheme.accentColor,
        canvasColor: subdomainConfig.canvasColor || defaultTheme.canvasColor,
        surfaceColor: subdomainConfig.surfaceColor || defaultTheme.surfaceColor,
        textColor: subdomainConfig.textColor || defaultTheme.textColor,
        fontFamily: subdomainConfig.fontFamily || defaultTheme.fontFamily,
        borderRadius,
        shadows: defaultTheme.shadows, // Can be customized per partner
      };

      applyTheme(whitelabelTheme);
      // Mark the active template on <html> so template-scoped styling
      // (e.g. the club's subtle branding accents) can hang off one class.
      document.documentElement.classList.toggle(
        "tpl-club",
        template.id === "club",
      );
      document.documentElement.classList.toggle(
        "tpl-institution",
        template.id === "institution",
      );
    } else {
      resetTheme();
    }

    return () => {
      resetTheme();
      document.documentElement.classList.remove("tpl-club", "tpl-institution");
    };
  }, [isBrandedSubdomain, subdomainConfig, template.id]);

  return <>{children}</>;
};
